import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, Card, CardActionArea, CardContent, CircularProgress, Grid, Typography } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCatalog } from './apps-catalog';
import { useUserAppsContext } from './user-apps-context';
import useHoverNavigation from './use-hover-navigation';
import { hoverHighlightSx } from './hover-style';
import AppsActionBarWithSettings from './apps-action-bar-with-settings';

export default function Welcome() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { activatedApps } = useUserAppsContext();
    const { categories, loading } = useCatalog();
    const [actionBarActions, setActionBarActions] = useState([]);

    const filteredCategories = useMemo(() =>
        categories
            .map(c => ({ ...c, apps: c.apps.filter(a => activatedApps.has(a.route)) }))
            .filter(c => c.apps.length > 0),
        [activatedApps, categories],
    );

    const allApps = useMemo(() => filteredCategories.flatMap(c => c.apps), [filteredCategories]);

    const itemCount = allApps.length + actionBarActions.length;

    const onSelect = useCallback((index) => {
        if (index < allApps.length) {
            // App card clicked
            const app = allApps[index];
            if (app?.route) navigate(app.route);
        } else {
            // Action bar item clicked
            const actionIndex = index - allApps.length;
            if (actionIndex >= 0 && actionIndex < actionBarActions.length) {
                actionBarActions[actionIndex].onClick();
            }
        }
    }, [navigate, allApps, actionBarActions]);

    const { active, activeIndex } = useHoverNavigation(itemCount, onSelect);

    let flatIndex = 0;

    return (
        <Box sx={{ p: 0, position: 'relative', minHeight: '100%' }}>
            {/* Action bar */}
            <AppsActionBarWithSettings
                customActions={[]}
                helperActions={[]}
                activeIndex={activeIndex - allApps.length}
                active={active && activeIndex >= allApps.length}
                onActionsChange={setActionBarActions}
                flex="0 0 auto"
            />

            <Typography variant="h4" gutterBottom>
                {t('APPLICATIONS.WELCOME_TITLE')}
            </Typography>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && filteredCategories.length === 0 && (
                <Box sx={{ textAlign: 'center', mt: 6 }}>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        {t('APPLICATIONS.NO_ACTIVE_APPS')}
                    </Typography>
                    <Button variant="contained" startIcon={<StorefrontIcon />} onClick={() => navigate('store')}>
                        {t('APPLICATIONS.EXPLORE_STORE')}
                    </Button>
                </Box>
            )}

            {filteredCategories.map((category) => {
                const standardApps = category.apps.filter(a => !a.ai);
                const aiApps = category.apps.filter(a => a.ai);

                return (
                    <Box key={category.labelKey} sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            {t(category.labelKey)}
                        </Typography>
                        {standardApps.length > 0 && (
                            <Grid container spacing={3}>
                                {standardApps.map((app) => {
                                    const index = flatIndex++;
                                    const Icon = app.icon;
                                    const isHovered = active && index === activeIndex;
                                    const cardSx = {
                                        transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                                        ...(isHovered && hoverHighlightSx),
                                    };
                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={app.titleKey}>
                                            <Card sx={cardSx}>
                                                <CardActionArea onClick={() => navigate(app.route)}>
                                                    <CardContent sx={{ textAlign: 'center' }}>
                                                        <Icon sx={{ fontSize: 48, mb: 1, color: 'primary.main' }} />
                                                        <Typography variant="h6">
                                                            {t(app.titleKey)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                            {t(app.descKey)}
                                                        </Typography>
                                                    </CardContent>
                                                </CardActionArea>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                        {aiApps.length > 0 && (
                            <Grid container spacing={3} sx={{ mt: standardApps.length > 0 ? 1 : 0 }}>
                                {aiApps.map((app) => {
                                    const index = flatIndex++;
                                    const Icon = app.icon;
                                    const isHovered = active && index === activeIndex;
                                    const cardSx = {
                                        transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                                        ...(isHovered && hoverHighlightSx),
                                    };
                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={app.titleKey}>
                                            <Card sx={cardSx}>
                                                <CardActionArea onClick={() => navigate(app.route)}>
                                                    <CardContent sx={{ textAlign: 'center' }}>
                                                        <Icon sx={{ fontSize: 48, mb: 1, color: 'primary.main' }} />
                                                        <Typography variant="h6">
                                                            {t(app.titleKey)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                            {t(app.descKey)}
                                                        </Typography>
                                                    </CardContent>
                                                </CardActionArea>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}
